import React from "react";
import { useTranslation } from "react-i18next";
import { ChatMessage } from "@/store/chat";
import cls from "./DebugPanel.module.scss";

interface Props {
  meta: NonNullable<ChatMessage["meta"]>;
}

const fmt = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

/**
 * «На чём стоит ответ» — для внутреннего показа (`?debug=1`).
 * Показывает ровно то, что вернул ассистент в `context`, без пересчёта.
 */
export const DebugPanel: React.FC<Props> = ({ meta }) => {
  const { t } = useTranslation();
  const ctx = meta.context;

  return (
    <div className={cls.panel}>
      <div className={cls.row}>
        <span className={cls.tag}>{meta.branch}</span>
        <span className={cls.tag}>{meta.intent}</span>
        {meta.blocked && <span className={cls.tagBad}>barrier: {meta.blocked}</span>}
        {meta.why && <span className={cls.why}>{meta.why}</span>}
      </div>
      {ctx && (
        <>
          <div className={cls.h}>{t("chat.debugData")}</div>
          <div className={cls.kv}>
            <span>заказы до</span>
            <b>{fmt(ctx.data.orders_until_utc)}</b>
            <span>доход до</span>
            <b>
              {fmt(ctx.data.income_until_utc)}
              {ctx.data.income_lag_hours != null && ` (−${Math.round(ctx.data.income_lag_hours)} ч)`}
            </b>
            <span>окно</span>
            <b>{ctx.data.days} сут.</b>
            <span>числа</span>
            <b>{meta.numbers.join(", ") || "—"}</b>
          </div>
          {ctx.steps.length > 0 && (
            <>
              <div className={cls.h}>{t("chat.debugSteps")}</div>
              <ol className={cls.steps}>
                {ctx.steps.map((s, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={i}>{s}</li>
                ))}
              </ol>
            </>
          )}
          {ctx.signals.length > 0 && (
            <>
              <div className={cls.h}>{t("chat.debugSignals")}</div>
              {ctx.signals.map((sg, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className={cls.signal} key={i}>
                  <b>{sg.kind_ru}</b>
                  {sg.place && ` · ${sg.place}`} · свидетелей {sg.witnesses} · уверенность{" "}
                  {Math.round(sg.confidence * 100)}%{sg.upcoming && " · анонс"}
                  {sg.evidence?.map((e, j) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div className={cls.quote} key={j}>
                      «{e.text}» — {e.chat}
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
};
